import { CenteredCircularProgress } from '../common/CenteredCircularProgress';
import { Box, Stack } from '@mui/material';
import { AiChat, ChatShort } from '../common/ai-chat/aiChatTypes';
import { NoResults } from '../documentation/shared/NoResults';
import { useChatSections } from './useChatSections';
import ChatsSectionLayout from './ChatsSectionLayout';
import AgentChatListItemSearch from './AgentChatListItemSearch';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { useRef, useMemo } from 'react';
import React from 'react';

const MIN_VIRTUALIZED_CHAT_ITEMS = 30;

const convertToShorChat: (items?: AiChat[]) => ChatShort[] | undefined = (items) =>
  items?.map((item) => ({ isLoading: false, ...item }));

const AgentSearchResults = ({
  isLoadingSearch,
  searchResults,
  searchText,
  activeChatId,
  onSelectActiveChat,
}: {
  isLoadingSearch: boolean;
  searchResults?: AiChat[];
  searchText?: string;
  activeChatId?: string;
  onSelectActiveChat: (id: string) => Promise<void>;
}) => {
  const noResults = !!searchText && !isLoadingSearch && searchResults && searchResults.length === 0;
  const sections = useChatSections(convertToShorChat(searchResults));
  const virtuosoHandle = useRef<VirtuosoHandle>(null);

  // Create lookup map for better performance instead of repeated find() calls
  const searchResultsMap = useMemo(() => {
    const map = new Map<string, AiChat>();
    searchResults?.forEach((result) => map.set(result.id, result));
    return map;
  }, [searchResults]);
  // Flatten all search result items for virtualization
  const allSearchItems = useMemo(() => {
    const items: Array<
      | {
          type: 'section-header';
          sectionTitle: string;
        }
      | {
          type: 'chat';
          chat: AiChat;
          sectionTitle: string;
        }
    > = [];

    sections.forEach((section) => {
      if (section.items.length === 0) return;

      items.push({
        type: 'section-header',
        sectionTitle: section.title,
      });

      section.items.forEach((chatItem) => {
        const relevantChat = searchResultsMap.get(chatItem.id);
        if (relevantChat) {
          items.push({
            type: 'chat',
            chat: relevantChat,
            sectionTitle: section.title,
          });
        }
      });
    });

    return items;
  }, [sections, searchResultsMap]);

  const totalChatCount = useMemo(() => {
    return sections.reduce((total, section) => total + section.items.length, 0);
  }, [sections]);

  const shouldVirtualize = totalChatCount > MIN_VIRTUALIZED_CHAT_ITEMS;

  const renderVirtualizedItem = (index: number, item: (typeof allSearchItems)[0]) => {
    if (item.type === 'section-header') {
      return (
        <ChatsSectionLayout key={`${item.sectionTitle}-header`} title={item.sectionTitle}>
          <div />
        </ChatsSectionLayout>
      );
    }

    return (
      <div key={item.chat.id} style={{ paddingBottom: '8px' }}>
        <AgentChatListItemSearch
          chat={item.chat}
          isActive={activeChatId === item.chat.id}
          onClick={() => onSelectActiveChat(item.chat.id)}
          searchText={searchText}
        />
      </div>
    );
  };

  const renderNonVirtualizedResults = () => (
    <Stack gap={1}>
      {sections.map((section) =>
        section.items.length > 0 ? (
          <ChatsSectionLayout key={section.title} title={section.title}>
            <Stack gap={1}>
              {section.items.map((chatItem) => {
                const relevantChat = searchResultsMap.get(chatItem.id);
                if (!relevantChat) return null;
                return (
                  <AgentChatListItemSearch
                    key={relevantChat.id}
                    chat={relevantChat}
                    isActive={activeChatId === relevantChat.id}
                    onClick={() => onSelectActiveChat(relevantChat.id)}
                    searchText={searchText}
                  />
                );
              })}
            </Stack>
          </ChatsSectionLayout>
        ) : null,
      )}
    </Stack>
  );

  if (noResults) {
    return (
      <Box pr={1} pl={2}>
        <NoResults />
      </Box>
    );
  }

  return (
    <div style={{ flex: 1, minHeight: 0 }}>
      {isLoadingSearch && <CenteredCircularProgress />}
      {!isLoadingSearch && (
        <>
          {!shouldVirtualize && renderNonVirtualizedResults()}
          {shouldVirtualize && (
            <div style={{ height: '100%', overflow: 'auto' }}>
              <Virtuoso
                ref={virtuosoHandle}
                data={allSearchItems}
                itemContent={renderVirtualizedItem}
                style={{ height: '100%' }}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AgentSearchResults;
